import { getColumns, drawerFilters } from './helpers'
import { Add as AddIcon } from '@mui/icons-material'
import { useCallback, useMemo, useState } from 'react'
import { toTableRowItems } from './serializers'
import { AdminStoreForm } from './AdminStoreForm'
import s from '../../../commonStyles.modules.scss'
import { useNotification } from 'hooks'
import { makeCoreGetRequest } from 'utils/api'
import { toTableGETUrl } from 'components/core-ui/Table/serializers'
import { formatErrorMessage } from 'utils/formatting'
import { Table } from 'components/core-ui/Table'
import { EditActionIcon } from 'icons'
import { useQuery } from 'react-query'
import { AutocompleteOption } from 'components/volumetry/interfaces'
import StoreIntervalModal from './StoreIntervalModal/StoreIntervalModal'
import {
  getAllShopOptions,
  getParentSocietyOptions,
  getRegionOptions,
  getScreenPermissions,
  getShopOptions,
  getShopTypesOptions
} from "../../api";
import { SCREENS } from '../../constants'

const AdminStore = () => {
  const { showNotification } = useNotification()

  const [tableItems, setTableItems] = useState<AdminStore[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [selectedRow, setSelectedRow] = useState<AdminStore>()
  const [shouldRefreshData, setShouldRefreshData] = useState<boolean>(false)
  const [modal, setModal] = useState(null)

  const openModal = (action): void => setModal(action)
  const closeModal = () => setModal(null)

  const { data: permissions } = getScreenPermissions(showNotification, SCREENS.ADMIN_SHOP)

  const selectRow = useCallback(
    (rowId: number | string) => {
      const row = tableItems.find(item => item.id === rowId)
      row && setSelectedRow(row)
    },
    [tableItems]
  )

  const columns = useMemo(
    () => getColumns(setModal, selectRow, permissions?.canView, permissions?.canViewHourlyInterval),
    [permissions, selectRow]
  )

  const fetchData = async ({ page, rowsPerPage, order, orderBy, tableFilters }) => {
    setIsLoading(true)
    try {
      const response = await makeCoreGetRequest(
        toTableGETUrl({
          url: '/shop-reception/shops',
          tableFilters,
          page,
          rowsPerPage,
          order,
          orderBy
        })
      )

      setTableItems(toTableRowItems(response.list))
      setTotalCount(response.page.totalCount)
      setIsLoading(false)
    } catch (error) {
      showNotification({
        message: `A aparut o eroare la obtinerea datelor. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  const { data: shopCodes } = getAllShopOptions(showNotification)

  const { data: metiShops } = useQuery(
    ['meti-shops'],
    async () => {
      const result = await makeCoreGetRequest('/shop-reception/shops/meti')

      const resultOptions = result.map(
        (item: any): AutocompleteOption => ({
          label: item.displayName,
          value: item.code
        })
      )
      return resultOptions || []
    },
    {
      onError: error => {
        showNotification({
          message: `A aparut o eroare la obtinerea datelor. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  )

  const { data: metiVersions } = useQuery(
    ['meti-versions'],
    async () => {
      const result = await makeCoreGetRequest('/shop-reception/shops/meti-version')
      const resultOptions = result.map(
        (item: string): AutocompleteOption => ({
          label: item,
          value: item
        })
      )
      return resultOptions || []
    },
    {
      onError: error => {
        showNotification({
          message: `A aparut o eroare la obtinerea datelor. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  )

  const { data: shopTypes } = getShopTypesOptions(showNotification)

  const { data: parentSocieties } = getParentSocietyOptions(showNotification)

  const { data: regions } = getRegionOptions(showNotification)

  const filteredParentSocieties = (parentSocieties || []).filter(
    society => society.label !== 'Artima' && society.label !== 'Columbus'
  )

  return (
    <div className={s.mainContainer}>
      <AdminStoreForm
        isOpen={modal === 'create'}
        closeModal={closeModal}
        shopCodes={shopCodes}
        metiVersions={metiVersions}
        parentSocieties={parentSocieties}
        metiShops={metiShops}
        setShouldRefreshData={setShouldRefreshData}
        permissions={permissions}
      />
      {modal === 'update' && selectedRow && (
        <AdminStoreForm
          isOpen={modal === 'update'}
          closeModal={closeModal}
          editItem={selectedRow}
          isEdit
          shopCodes={shopCodes}
          metiVersions={metiVersions}
          parentSocieties={parentSocieties}
          metiShops={metiShops}
          setShouldRefreshData={setShouldRefreshData}
          permissions={permissions}
        />
      )}

      {selectedRow && (
        <StoreIntervalModal
          isOpen={modal === 'interval'}
          closeModal={closeModal}
          item={selectedRow}
          canEditHourlyInterval={permissions?.canEditHourlyInterval}
        />
      )}
      <Table
        hideCheckbox
        orderBy="code"
        order="asc"
        columns={columns}
        items={tableItems}
        fetchData={fetchData}
        isLoading={isLoading}
        totalCount={totalCount}
        shouldRefreshData={shouldRefreshData}
        setShouldRefreshData={setShouldRefreshData}
        rowActions={
          permissions?.canEdit
            ? [
                {
                  label: 'Edit',
                  labelColor: 'white',
                  typographyVariant: 'medium',
                  backgroundColor: '#16a085',
                  desktopIcon: <EditActionIcon />,
                  onClick: (_, rowId) => {
                    selectRow(rowId)
                    setModal('update')
                  }
                }
              ]
            : []
        }
        rowActionsWidth="130px"
        toolbarActions={
          permissions?.canAdd
            ? [
                {
                  label: 'Magazin',
                  icon: <AddIcon />,
                  onClick: () => openModal('create')
                }
              ]
            : []
        }
        filters={drawerFilters(
          shopCodes,
          regions,
          metiVersions,
          shopTypes,
          filteredParentSocieties
        )}
        filtersButtonLabel="Filtre"
        filtersLabel="Filtre"
        appliedFiltersLabel="Filtre aplicate"
        cancelFiltersButtonLabel="Anuleaza"
        applyFiltersButtonLabel="Aplica"
        rowsPerPage={10}
        rowsPerPageOptions={[10, 25, 50]}
      />
    </div>
  )
}

export default AdminStore
